import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CreditCardIcon, 
  ShieldCheckIcon, 
  CheckCircleIcon, 
  ClockIcon, 
  ArrowLeftIcon,
  StarIcon,
  GiftIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { useCurrency } from '../contexts/CurrencyContext';

declare global {
  interface Window {
    FlywirePayment: any;
    grecaptcha: any;
  }
}

// reCAPTCHA verification function
const verifyRecaptcha = async (): Promise<boolean> => {
  try {
    if (!window.grecaptcha) {
      console.error('reCAPTCHA not loaded');
      return false;
    }

    const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
    if (!siteKey) {
      console.error('reCAPTCHA site key not configured');
      return false;
    }

    // Execute reCAPTCHA
    const token = await window.grecaptcha.execute(siteKey, { action: 'flywire_payment' });
    
    // Verify token with backend
    const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/payment/verify-recaptcha`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ token })
    });

    const result = await response.json();
    return result.success;
  } catch (error) {
    console.error('reCAPTCHA verification failed:', error);
    return false;
  }
};

interface PaymentFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  maritalStatus: string;
  agreeToTerms: boolean;
}

interface PackageData {
  _id: string;
  name: string;
  packageCode: string;
  priceUSD: number;
  priceAUD: number;
  validityPeriod: number;
  maxGuests: number;
  resorts: string[];
  inclusions: string[];
  isActive: boolean;
  isLive: boolean;
}

const PaymentPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currency, formatPrice, getPrice } = useCurrency();
  const [isProcessing, setIsProcessing] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState('30:00');
  const [formData, setFormData] = useState<PaymentFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    maritalStatus: '',
    agreeToTerms: false
  });
  const [packageData, setPackageData] = useState<PackageData | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load package data from API
  useEffect(() => {
    const fetchPackageData = async () => {
      if (!id) {
        console.error('No package ID provided');
        return;
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/packages/${id}`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch package data');
        }

        const packageData = await response.json();
        
        // Check if package is live and active before allowing payment
        if (!packageData.isLive || !packageData.isActive) {
          alert('This package is no longer available for purchase.');
          navigate('/packages');
          return;
        }
        
        setPackageData({
          _id: packageData._id,
          name: packageData.name,
          packageCode: packageData.packageCode,
          priceUSD: packageData.priceUSD,
          priceAUD: packageData.priceAUD,
          validityPeriod: packageData.validityPeriod,
          maxGuests: packageData.maxGuests,
          resorts: packageData.resorts ? packageData.resorts.map((resort: any) => resort.name) : [],
          inclusions: packageData.inclusions || [],
          isActive: packageData.isActive,
          isLive: packageData.isLive
        });
      } catch (error) {
        console.error('Error fetching package data:', error);
        alert('Failed to load package details. Please try again.');
        navigate('/packages');
      }
    };

    fetchPackageData();
  }, [id, navigate]);

  // Timer countdown
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        const [minutes, seconds] = prev.split(':').map(Number);
        const totalSeconds = minutes * 60 + seconds;
        
        if (totalSeconds <= 1) {
          clearInterval(timer);
          return '0:00';
        }
        
        const newTotal = totalSeconds - 1;
        const newMinutes = Math.floor(newTotal / 60);
        const newSeconds = newTotal % 60;
        return `${newMinutes}:${newSeconds.toString().padStart(2, '0')}`;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Form validation
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = 'Date of birth is required';
    } else {
      const birthDate = new Date(formData.dateOfBirth);
      const today = new Date();
      const age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        const actualAge = age - 1;
        if (actualAge < 30) {
          newErrors.dateOfBirth = 'You must be at least 30 years old';
        }
      } else if (age < 30) {
        newErrors.dateOfBirth = 'You must be at least 30 years old';
      }
    }

    if (!formData.maritalStatus) {
      newErrors.maritalStatus = 'Marital status is required';
    }

    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle Flywire payment submission
  const handlePayment = async () => {
    if (!validateForm()) {
      return;
    }

    if (!packageData) {
      alert('Package data not loaded. Please refresh the page.');
      return;
    }

    setIsProcessing(true);

    try {
      // Verify reCAPTCHA first
      const recaptchaValid = await verifyRecaptcha();
      if (!recaptchaValid) {
        alert('Security verification failed. Please try again.');
        setIsProcessing(false);
        return;
      }

      const amount = getPrice(packageData.priceUSD, packageData.priceAUD);
      
      // Determine recipient code and callback URL based on currency
      let recipientCode: string, callbackUrl: string;
      
      switch (currency) {
        case 'AUD':
          recipientCode = 'OFT';
          callbackUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/payment/webhook/aud`;
          break;
        case 'USD':
          recipientCode = 'KQT';
          callbackUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/payment/webhook/usd`;
          break;
        default:
          alert('Unsupported currency selected. Please select a valid currency.');
          setIsProcessing(false);
          return;
      }

      // Generate unique transaction ID
      const transactionId = `TXN${Date.now()}`;
      
      // Construct return URL with all required parameters for booking creation
      const returnUrl = `${window.location.origin}/payment-success?status=pending&reference=${transactionId}&transactionId=${transactionId}&email=${encodeURIComponent(formData.email)}&name=${encodeURIComponent(`${formData.firstName} ${formData.lastName}`)}&amount=${amount}&currency=${currency}&package=${encodeURIComponent(packageData.name)}&packageId=${encodeURIComponent(packageData._id)}&phone=${encodeURIComponent(formData.phone)}&paymentMethod=Flywire`;

      // Flywire payment configuration
      const config = {
        env: 'prod',
        recipientCode: recipientCode,
        amount: parseFloat(amount.toString()),
        currency: currency.toUpperCase(),
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        recipientFields: {
          booking_reference: transactionId,
          web_source: 'baligetaway.com',
          package_id: packageData._id,
          package_name: packageData.name
        },
        requestPayerInfo: true,
        requestRecipientInfo: false,
        returnUrl: returnUrl,
        callbackUrl: callbackUrl,
        callbackId: transactionId,
        callbackVersion: '2',
        onInvalidInput: function(errors: any[]) {
          errors.forEach(function(error: any) {
            alert(error.msg);
          });
          setIsProcessing(false);
        },
        // Handle successful payment completion
        onSuccess: async (result: any) => {
          console.log('✅ Flywire payment success:', result);
          try {
            const flywirePaymentId = result?.paymentId || result?.payment_id || result?.id || transactionId;
            
            // Create booking record immediately
            const bookingResponse = await axios.post('/api/bookings/create', {
              firstName: formData.firstName,
              lastName: formData.lastName,
              email: formData.email,
              phone: formData.phone,
              amount: amount,
              currency: currency,
              packageId: packageData._id,
              packageName: packageData.name,
              transactionId: flywirePaymentId,
              paymentMethod: 'flywire'
            });
            
            console.log('✅ Booking created:', bookingResponse.data);
            
            // Redirect to success page
            const successUrl = `/payment-success?status=success&transactionId=${flywirePaymentId}&reference=${bookingResponse.data.booking?.bookingCode || transactionId}&email=${formData.email}&name=${encodeURIComponent(formData.firstName + ' ' + formData.lastName)}&amount=${amount}&currency=${currency}&package=${encodeURIComponent(packageData.name)}&paymentMethod=Flywire`;
            window.location.href = successUrl;
          } catch (bookingError: any) {
            console.error('❌ Failed to create booking after payment:', bookingError);
            const successUrl = `/payment-success?status=success&transactionId=${transactionId}&email=${formData.email}&name=${encodeURIComponent(formData.firstName + ' ' + formData.lastName)}&amount=${amount}&currency=${currency}&package=${encodeURIComponent(packageData.name)}&paymentMethod=Flywire`;
            window.location.href = successUrl;
          }
        },
        // Handle payment cancellation
        onCancel: () => {
          console.log('❌ Flywire payment cancelled by user');
          alert('Payment was cancelled. Please try again when ready.');
          setIsProcessing(false);
        }
      };

      // Check if Flywire module is loaded
      if (typeof window.FlywirePayment === 'undefined') {
        alert('Flywire payment module is not loaded. Please try again later.');
        setIsProcessing(false);
        return;
      }

      // Initiate Flywire modal
      const modal = window.FlywirePayment.initiate(config);
      if (!modal) {
        alert('Failed to initiate payment modal. Please try again.');
        setIsProcessing(false);
        return;
      }
      modal.render();

      // Reset button state if user closes modal without paying
      setTimeout(() => {
        setIsProcessing(false);
      }, 3000);

    } catch (error) {
      console.error('Payment error:', error);
      alert('An error occurred during payment. Please try again.');
      setIsProcessing(false);
    }
  };

  if (!packageData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading package details...</p>
        </div>
      </div>
    );
  }

  const currentPrice = getPrice(packageData.priceUSD, packageData.priceAUD);
  const formattedPrice = formatPrice(packageData.priceUSD, packageData.priceAUD);

  return (
    <>
      <Helmet>
        <title>Payment - {packageData.name} | Bali Getaway</title>
        <meta name="description" content={`Complete your booking for ${packageData.name}`} />
        <script src="https://checkout.flywire.com/flywire-payment.js" />
        <script src={`https://www.google.com/recaptcha/api.js?render=${process.env.REACT_APP_RECAPTCHA_SITE_KEY}`} />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-orange-50 to-red-50 py-12 px-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-7xl mx-auto"
        >
          {/* Header */}
          <div className="mb-12">
            <Link
              to="/packages"
              className="inline-flex items-center text-orange-600 hover:text-orange-700 font-black text-sm mb-8 group tracking-widest uppercase transition-all"
            >
              <ArrowLeftIcon className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
              Back to Packages
            </Link>
            
            <div className="bg-white rounded-[2.5rem] shadow-2xl p-8 lg:p-12 border border-gray-100 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-orange-50 rounded-full -mr-32 -mt-32 opacity-50"></div>
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8 relative z-10">
                <div>
                  <h1 className="text-4xl lg:text-6xl font-black text-gray-900 tracking-tight leading-none">
                    Finalize Your <span className="bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">Stay</span>
                  </h1>
                  <p className="text-xl text-gray-500 font-medium mt-4">Secure payment for {packageData.name}</p>
                </div>
                <div className="flex-shrink-0">
                  <div className="bg-orange-50 px-8 py-4 rounded-[2rem] border border-orange-100 shadow-sm inline-flex items-center gap-4">
                    <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center shadow-sm">
                      <ClockIcon className="w-6 h-6 text-orange-600 animate-pulse" />
                    </div>
                    <div>
                      <p className="text-[10px] font-black text-orange-400 uppercase tracking-widest">Time Remaining</p>
                      <span className="text-2xl font-black text-orange-600 font-mono">{timeRemaining}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Form */}
            <div className="lg:col-span-2 space-y-8">
              <div className="bg-white rounded-[3rem] shadow-2xl p-10 lg:p-12 border border-gray-100">
                <div className="flex items-center gap-3 text-gray-900 font-black mb-10 pb-4 border-b border-gray-100 uppercase tracking-tight">
                  <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                    <StarIcon className="w-6 h-6 text-orange-600" />
                  </div>
                  <h2 className="text-2xl">Guest Information</h2>
                </div>
                
                <form className="space-y-8">
                  {/* Name Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-2">
                      <label htmlFor="firstName" className="text-xs font-black text-gray-500 uppercase ml-1 tracking-widest">
                        First Name *
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        className={`w-full px-6 py-4 bg-gray-50 border-none rounded-[1.5rem] focus:ring-4 focus:ring-orange-500/10 focus:bg-white transition-all text-gray-900 font-bold placeholder:text-gray-400 ${
                          errors.firstName ? 'ring-2 ring-red-500' : ''
                        }`}
                        placeholder="John"
                      />
                      {errors.firstName && (
                        <p className="mt-1 text-xs text-red-600 font-bold uppercase tracking-tighter flex items-center gap-1">
                          <ExclamationTriangleIcon className="w-3 h-3" />
                          {errors.firstName}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="lastName" className="text-xs font-black text-gray-500 uppercase ml-1 tracking-widest">
                        Last Name *
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        className={`w-full px-6 py-4 bg-gray-50 border-none rounded-[1.5rem] focus:ring-4 focus:ring-orange-500/10 focus:bg-white transition-all text-gray-900 font-bold placeholder:text-gray-400 ${
                          errors.lastName ? 'ring-2 ring-red-500' : ''
                        }`}
                        placeholder="Doe"
                      />
                      {errors.lastName && (
                        <p className="mt-1 text-xs text-red-600 font-bold uppercase tracking-tighter flex items-center gap-1">
                          <ExclamationTriangleIcon className="w-3 h-3" />
                          {errors.lastName}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Email & Phone */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-2">
                      <label htmlFor="email" className="text-xs font-black text-gray-500 uppercase ml-1 tracking-widest">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`w-full px-6 py-4 bg-gray-50 border-none rounded-[1.5rem] focus:ring-4 focus:ring-orange-500/10 focus:bg-white transition-all text-gray-900 font-bold placeholder:text-gray-400 ${
                          errors.email ? 'ring-2 ring-red-500' : ''
                        }`}
                        placeholder="john@example.com"
                      />
                      {errors.email && (
                        <p className="mt-1 text-xs text-red-600 font-bold uppercase tracking-tighter flex items-center gap-1">
                          <ExclamationTriangleIcon className="w-3 h-3" />
                          {errors.email}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="phone" className="text-xs font-black text-gray-500 uppercase ml-1 tracking-widest">
                        Mobile Number *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className={`w-full px-6 py-4 bg-gray-50 border-none rounded-[1.5rem] focus:ring-4 focus:ring-orange-500/10 focus:bg-white transition-all text-gray-900 font-bold placeholder:text-gray-400 ${
                          errors.phone ? 'ring-2 ring-red-500' : ''
                        }`}
                        placeholder="+61 412 345 678"
                      />
                      {errors.phone && (
                        <p className="mt-1 text-xs text-red-600 font-bold uppercase tracking-tighter flex items-center gap-1">
                          <ExclamationTriangleIcon className="w-3 h-3" />
                          {errors.phone}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Date of Birth & Marital Status */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-2">
                      <label htmlFor="dateOfBirth" className="text-xs font-black text-gray-500 uppercase ml-1 tracking-widest">
                        Date of Birth * (30+ years)
                      </label>
                      <input
                        type="date"
                        id="dateOfBirth"
                        name="dateOfBirth"
                        value={formData.dateOfBirth}
                        onChange={handleInputChange}
                        max={new Date(new Date().setFullYear(new Date().getFullYear() - 30)).toISOString().split('T')[0]}
                        className={`w-full px-6 py-4 bg-gray-50 border-none rounded-[1.5rem] focus:ring-4 focus:ring-orange-500/10 focus:bg-white transition-all text-gray-900 font-bold ${
                          errors.dateOfBirth ? 'ring-2 ring-red-500' : ''
                        }`}
                      />
                      {errors.dateOfBirth && (
                        <p className="mt-1 text-xs text-red-600 font-bold uppercase tracking-tighter flex items-center gap-1">
                          <ExclamationTriangleIcon className="w-3 h-3" />
                          {errors.dateOfBirth}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <label htmlFor="maritalStatus" className="text-xs font-black text-gray-500 uppercase ml-1 tracking-widest">
                        Marital Status *
                      </label>
                      <select
                        id="maritalStatus"
                        name="maritalStatus"
                        value={formData.maritalStatus}
                        onChange={handleInputChange}
                        className={`w-full px-6 py-4 bg-gray-50 border-none rounded-[1.5rem] focus:ring-4 focus:ring-orange-500/10 focus:bg-white transition-all text-gray-900 font-bold ${
                          errors.maritalStatus ? 'ring-2 ring-red-500' : ''
                        }`}
                      >
                        <option value="">Choose Status</option>
                        <option value="married">Married</option>
                        <option value="coliving">Coliving</option>
                      </select>
                      {errors.maritalStatus && (
                        <p className="mt-1 text-xs text-red-600 font-bold uppercase tracking-tighter flex items-center gap-1">
                          <ExclamationTriangleIcon className="w-3 h-3" />
                          {errors.maritalStatus}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Terms and Conditions */}
                  <div className="bg-gray-50 rounded-[2rem] p-8 border border-gray-100">
                    <div className="flex items-start gap-4">
                      <div className="pt-1">
                        <input
                          type="checkbox"
                          id="agreeToTerms"
                          name="agreeToTerms"
                          checked={formData.agreeToTerms}
                          onChange={handleInputChange}
                          className="w-6 h-6 text-orange-600 border-gray-300 rounded-lg focus:ring-orange-500 cursor-pointer"
                        />
                      </div>
                      <label htmlFor="agreeToTerms" className="text-sm text-gray-600 font-medium leading-relaxed cursor-pointer">
                        I confirm that all guest information provided is accurate and I agree to the{' '}
                        <Link to="/terms" target="_blank" className="text-orange-600 font-black hover:underline">Terms & Conditions</Link>
                        {' '}and{' '}
                        <Link to="/privacy" target="_blank" className="text-orange-600 font-black hover:underline">Privacy Policy</Link>.
                      </label>
                    </div>
                    {errors.agreeToTerms && (
                      <p className="mt-4 text-xs text-red-600 font-bold uppercase tracking-tighter flex items-center gap-1">
                        <ExclamationTriangleIcon className="w-3 h-3" />
                        {errors.agreeToTerms}
                      </p>
                    )}
                  </div>
                </form>
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-[3rem] shadow-2xl p-8 sticky top-12 border border-gray-100 overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-orange-500 to-amber-500"></div>
                <h2 className="text-2xl font-black text-gray-900 mb-8 tracking-tight uppercase">Booking Summary</h2>
                
                <div className="space-y-8">
                  <div className="bg-gray-50 rounded-3xl p-6 border border-gray-100">
                    <h3 className="font-black text-gray-900 text-lg leading-tight mb-2">{packageData.name}</h3>
                    <div className="flex flex-wrap gap-2 mt-4">
                      <span className="px-3 py-1 bg-white rounded-full text-[10px] font-black text-gray-400 border border-gray-100 uppercase tracking-widest">{packageData.packageCode}</span>
                      <span className="px-3 py-1 bg-orange-100 rounded-full text-[10px] font-black text-orange-600 uppercase tracking-widest">{packageData.validityPeriod} MONTHS</span>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest ml-1">Featured Resorts</h4>
                    <div className="grid grid-cols-1 gap-2">
                      {packageData.resorts.slice(0, 3).map((resort, index) => (
                        <div key={index} className="flex items-center gap-3 bg-gray-50/50 p-3 rounded-2xl border border-gray-100/50">
                          <div className="w-6 h-6 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <CheckCircleIcon className="w-4 h-4 text-green-600" />
                          </div>
                          <span className="text-sm font-bold text-gray-700">{resort}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest ml-1">Inclusions</h4>
                    <div className="grid grid-cols-1 gap-2">
                      {packageData.inclusions.slice(0, 4).map((inclusion, index) => (
                        <div key={index} className="flex items-center gap-3">
                          <GiftIcon className="w-4 h-4 text-orange-400 flex-shrink-0" />
                          <span className="text-sm font-medium text-gray-600">{inclusion}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="pt-8 border-t border-gray-100 space-y-6">
                    <div className="flex justify-between items-end">
                      <div>
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Total Price ({currency})</p>
                        <span className="text-4xl font-black text-orange-600 tracking-tighter">{formattedPrice}</span>
                      </div>
                      <div className="w-12 h-12 bg-gray-50 rounded-2xl flex items-center justify-center border border-gray-100">
                        <ShieldCheckIcon className="w-6 h-6 text-green-500" />
                      </div>
                    </div>

                    <button
                      onClick={handlePayment}
                      disabled={isProcessing}
                      className="w-full bg-gradient-to-r from-orange-500 to-amber-500 text-white font-black py-5 px-8 rounded-2xl hover:from-orange-600 hover:to-amber-600 transition-all duration-300 shadow-2xl shadow-orange-200 disabled:opacity-50 active:scale-[0.98] flex items-center justify-center gap-3 tracking-wider uppercase"
                    >
                      {isProcessing ? (
                        <>
                          <svg className="animate-spin h-6 w-6 text-white" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          <span>INITIALIZING...</span>
                        </>
                      ) : (
                        <>
                          <CreditCardIcon className="w-6 h-6" />
                          <span>PAY & BOOK NOW</span>
                        </>
                      )}
                    </button>

                    <div className="text-center">
                      <div className="inline-flex items-center gap-2 text-[10px] font-black text-gray-400 uppercase tracking-tighter bg-gray-50 px-4 py-2 rounded-full border border-gray-100">
                        <ShieldCheckIcon className="w-3.5 h-3.5 text-green-500" />
                        <span>Secured by Flywire Technology</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </>
  );
};

export default PaymentPage;
